import { RefObject, useEffect, useRef } from 'react';

const DEFAULT_HIDE_AFTER_PX = 24;
const DEFAULT_REVEAL_AFTER_PX = 8;
const COLLAPSED_ATTRIBUTE = 'data-collapsed';

interface CollapsingHeaderOptions {
  scrollAreaRef: RefObject<HTMLElement | null>;
  headerRef: RefObject<HTMLElement | null>;
  collapsibleRef: RefObject<HTMLElement | null>;
  /** downward travel, since the direction last flipped, required to hide the header */
  hideAfterPx?: number;
  /** upward travel, since the direction last flipped, required to bring it back */
  revealAfterPx?: number;
}

/**
 * Keeps `data-collapsed` on the header in sync with the scroll direction of its scroll area.
 *
 * Writing an attribute instead of returning React state keeps a whole scroll gesture at zero
 * re-renders, which matters in a narrow task pane rendering ~50 mounted cards.
 */
export const useCollapsingHeaderOnScroll = ({
  scrollAreaRef,
  headerRef,
  collapsibleRef,
  hideAfterPx = DEFAULT_HIDE_AFTER_PX,
  revealAfterPx = DEFAULT_REVEAL_AFTER_PX,
}: CollapsingHeaderOptions): void => {
  const lastScrollTopRef = useRef(0);
  const directionAnchorRef = useRef(0);
  const isScrollingDownRef = useRef(false);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    const header = headerRef.current;
    const collapsible = collapsibleRef.current;
    if (!scrollArea || !header || !collapsible) return;

    const applyCollapsedState = (isCollapsed: boolean) => {
      const nextValue = isCollapsed ? 'true' : 'false';
      if (header.getAttribute(COLLAPSED_ATTRIBUTE) === nextValue) return;
      header.setAttribute(COLLAPSED_ATTRIBUTE, nextValue);
    };

    // WKWebView and the Office web view rubber-band: scrollTop dips below 0 at the top and
    // past the maximum at the bottom. Clamping stops a bounce reading as a direction change.
    const readClampedScrollTop = () => {
      const maxScrollTop = Math.max(scrollArea.scrollHeight - scrollArea.clientHeight, 0);
      return Math.min(Math.max(scrollArea.scrollTop, 0), maxScrollTop);
    };

    const resolveHeaderState = () => {
      frameRef.current = null;

      const scrollTop = readClampedScrollTop();
      const previousScrollTop = lastScrollTopRef.current;
      lastScrollTopRef.current = scrollTop;

      if (scrollTop <= collapsible.offsetHeight) {
        directionAnchorRef.current = scrollTop;
        applyCollapsedState(false);
        return;
      }

      const delta = scrollTop - previousScrollTop;
      if (delta === 0) return;

      const isScrollingDown = delta > 0;
      if (isScrollingDown !== isScrollingDownRef.current) {
        isScrollingDownRef.current = isScrollingDown;
        directionAnchorRef.current = previousScrollTop;
      }

      // Measured from where the direction flipped, not from the previous frame, so a slow
      // one-pixel-per-frame drag still crosses the threshold instead of never hiding.
      const travelledPx = Math.abs(scrollTop - directionAnchorRef.current);
      if (travelledPx < (isScrollingDown ? hideAfterPx : revealAfterPx)) return;

      applyCollapsedState(isScrollingDown);
    };

    const scheduleHeaderState = () => {
      if (frameRef.current !== null) return;
      frameRef.current = requestAnimationFrame(resolveHeaderState);
    };

    // A control reached by keyboard must never sit behind a header that scrolled away.
    // Revealing rather than pinning is what keeps this safe: a pin would last for as long
    // as the control kept focus, so one click on the star or refresh button would freeze
    // the header for good.
    const revealForFocus = () => {
      directionAnchorRef.current = readClampedScrollTop();
      isScrollingDownRef.current = false;
      applyCollapsedState(false);
    };

    lastScrollTopRef.current = readClampedScrollTop();
    directionAnchorRef.current = lastScrollTopRef.current;
    scrollArea.addEventListener('scroll', scheduleHeaderState, { passive: true });
    header.addEventListener('focusin', revealForFocus);

    return () => {
      scrollArea.removeEventListener('scroll', scheduleHeaderState);
      header.removeEventListener('focusin', revealForFocus);
      if (frameRef.current === null) return;
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    };
  }, [scrollAreaRef, headerRef, collapsibleRef, hideAfterPx, revealAfterPx]);
};
